import { useState, useEffect, useCallback } from 'react';
import { estimateAPI } from '../../services/api';

/**
 * Formats a number as USD currency: $X,XXX.XX
 */
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-';
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Returns a human-readable relative time string (e.g. "2h ago", "3d ago").
 */
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const past = new Date(dateStr);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

/**
 * Maps source type to a display label.
 */
const sourceLabel = (source) => {
  if (!source) return 'Unknown';
  switch (source) {
    case 'spot_market': return 'Spot Market (FBX)';
    case 'own_model': return 'Own Model';
    default: return source.replace(/_/g, ' ');
  }
};

/**
 * EstimateBreakdown — shows the freight cost estimate for a shipment.
 *
 * Props:
 *   - shipmentId: UUID of the shipment to fetch/display estimate for
 */
const EstimateBreakdown = ({ shipmentId }) => {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  const fetchEstimate = useCallback(async () => {
    if (!shipmentId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await estimateAPI.getEstimate(shipmentId);
      setEstimate(result.data || null);
    } catch (err) {
      // 404 means no estimate yet — not an error
      if (err.response?.status === 404) {
        setEstimate(null);
      } else {
        setError('Failed to load estimate');
        console.error('EstimateBreakdown fetch error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      setError(null);
      await estimateAPI.generateEstimate({ shipmentId });
      // Refetch the latest estimate after generation
      await fetchEstimate();
    } catch (err) {
      setError('Failed to generate estimate');
      console.error('EstimateBreakdown regenerate error:', err);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="detail-section">
        <h2>Freight Estimate</h2>
        <p className="loading">Loading estimate...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail-section">
        <h2>Freight Estimate</h2>
        <div className="error-message">{error}</div>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleRegenerate}
          disabled={regenerating}
        >
          {regenerating ? 'Generating...' : 'Generate Estimate'}
        </button>
      </div>
    );
  }

  // Empty state — no estimate exists yet
  if (!estimate) {
    return (
      <div className="detail-section">
        <h2>Freight Estimate</h2>
        <div className="empty-state" style={{ padding: '1.5rem', boxShadow: 'none' }}>
          <p>No estimate available for this shipment yet.</p>
          <button
            className="btn btn-primary"
            onClick={handleRegenerate}
            disabled={regenerating}
          >
            {regenerating ? 'Generating...' : 'Generate Estimate'}
          </button>
        </div>
      </div>
    );
  }

  const breakdown = estimate.breakdown || {};
  const confidencePercent = estimate.confidence != null
    ? Math.round(estimate.confidence * 100)
    : null;

  return (
    <div className="detail-section estimate-breakdown">
      <h2>Freight Estimate</h2>

      {/* Line items */}
      <div className="estimate-lines">
        {breakdown.oceanFreight != null && (
          <div className="estimate-line">
            <span className="estimate-line-label">Ocean Freight</span>
            <span className="estimate-line-value">
              ~{formatCurrency(breakdown.oceanFreight)}
            </span>
            <span className="estimate-line-source badge badge-tag">
              {breakdown.oceanSource || 'market'}
            </span>
          </div>
        )}

        {breakdown.originTrucking != null && (
          <div className="estimate-line">
            <span className="estimate-line-label">Origin Trucking</span>
            <span className="estimate-line-value">
              ~{formatCurrency(breakdown.originTrucking)}
            </span>
            <span className="estimate-line-source badge badge-tag">formula</span>
          </div>
        )}

        {breakdown.destinationTrucking != null && (
          <div className="estimate-line">
            <span className="estimate-line-label">Destination Trucking</span>
            <span className="estimate-line-value">
              ~{formatCurrency(breakdown.destinationTrucking)}
            </span>
            <span className="estimate-line-source badge badge-tag">formula</span>
          </div>
        )}

        {breakdown.duties != null && (
          <div className="estimate-line">
            <span className="estimate-line-label">Duties (estimated)</span>
            <span className="estimate-line-value">
              ~{formatCurrency(breakdown.duties)}
            </span>
            <span className="estimate-line-source badge badge-tag">
              {breakdown.dutyRate != null ? `~${breakdown.dutyRate}%` : 'rate'}
            </span>
          </div>
        )}

        {/* Divider */}
        <div className="estimate-divider" />

        {/* Total */}
        <div className="estimate-line estimate-total-line">
          <span className="estimate-line-label">Total Estimated</span>
          <span className="estimate-line-value estimate-total-value">
            {estimate.totalLow != null && estimate.totalHigh != null
              ? `${formatCurrency(estimate.totalLow)} - ${formatCurrency(estimate.totalHigh)}`
              : formatCurrency(estimate.totalEstimated || estimate.total)}
          </span>
        </div>
      </div>

      {/* Confidence */}
      {confidencePercent != null && (
        <div className="estimate-confidence">
          <span className="estimate-confidence-label">Confidence:</span>
          <div className="estimate-confidence-bar">
            <div
              className="estimate-confidence-fill"
              style={{ width: `${confidencePercent}%` }}
            />
          </div>
          <span className="estimate-confidence-value">{confidencePercent}%</span>
        </div>
      )}

      {/* Footer: source + time */}
      <div className="estimate-footer">
        <span className="text-small">
          Source: {sourceLabel(estimate.source)}
          {estimate.createdAt && ` · Generated ${timeAgo(estimate.createdAt)}`}
        </span>
      </div>

      {/* Regenerate button */}
      <div style={{ marginTop: '0.75rem' }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={handleRegenerate}
          disabled={regenerating}
        >
          {regenerating ? 'Regenerating...' : 'Regenerate Estimate'}
        </button>
      </div>
    </div>
  );
};

export default EstimateBreakdown;
